import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import documentService from '../../services/documentService';
import Toast from '../../components/Common/Toast';
import ConfirmModal from '../../components/Common/ConfirmModal';
import TableWithMonthGroups from '../../components/Common/TableWithMonthGroups';
import { formatDateLocaleLima } from '../../utils/dateUtils';
import MutateGate from '../../components/Common/MutateGate';
import ViewOrEditLink from '../../components/Common/ViewOrEditLink';
import DocumentPdfButton from '../../components/Documents/DocumentPdfButton';
import BuyerUserCell from '../../components/Common/BuyerUserCell';
import ComprasProjectFilterSelect from '../../components/Common/ComprasProjectFilterSelect';
import { useModuleSettings } from '../../contexts/ModuleSettingsContext';
import { formatLineProjectsLabel } from '../../utils/lineProjectLabels';
import ComprasSiteFilterTabs from '../../components/Common/ComprasSiteFilterTabs';
import { useComprasSiteListParams, useComprasSiteViewState } from '../../utils/comprasPurchaseSite';
import IntegrationDocumentBadge from '../../components/Documents/IntegrationDocumentBadge';

const DOCUMENT_TYPE_LABELS = {
  factura: 'Factura',
  boleta: 'Boleta',
  recibo_por_honorarios: 'Recibo por Honorarios',
  nota_credito: 'Nota de Crédito',
  nota_debito: 'Nota de Débito',
  otro: 'Otro',
  suministro_continuo: 'Suministro continuo',
};

const formatDocumentType = (type) => DOCUMENT_TYPE_LABELS[type] || type || '—';

const STATUS_SEARCH_LABELS = {
  issued: 'emitido',
  open: 'abierto',
  completed: 'completado',
  cancelled: 'cancelado',
};

const sortDocumentsForList = (list) =>
  [...list].sort((a, b) => {
    const dateA = a?.issue_date ? new Date(a.issue_date).getTime() : 0;
    const dateB = b?.issue_date ? new Date(b.issue_date).getTime() : 0;
    if (dateA !== dateB) return dateB - dateA;

    const createdA = a?.created_at ? new Date(a.created_at).getTime() : 0;
    const createdB = b?.created_at ? new Date(b.created_at).getTime() : 0;
    if (createdA !== createdB) return createdB - createdA;

    return Number(b?.id || 0) - Number(a?.id || 0);
  });

const documentMatchesFilters = (doc, filters) => {
  const { searchTerm, statusFilter, documentTypeFilter, originFilter, dateFrom, dateTo, selectedSupplier, userFilter } = filters;

  if (originFilter === 'integration' && !doc.integration_source) return false;
  if (originFilter === 'manual' && doc.integration_source) return false;

  if (statusFilter !== 'all' && doc.status !== statusFilter) return false;

  if (documentTypeFilter === 'suministro_continuo' && doc.document_type !== 'suministro_continuo') {
    return false;
  }
  if (documentTypeFilter === 'normal' && doc.document_type === 'suministro_continuo') {
    return false;
  }

  if (userFilter !== 'all') {
    const creatorId = doc.creator?.id ?? doc.created_by;
    if (String(creatorId || '') !== String(userFilter)) return false;
  }

  if (selectedSupplier && String(doc.supplier_id) !== String(selectedSupplier)) return false;

  const issueDate = String(doc.issue_date || '').slice(0, 10);
  if (dateFrom && issueDate && issueDate < dateFrom) return false;
  if (dateTo && issueDate && issueDate > dateTo) return false;

  const query = searchTerm.trim().toLowerCase();
  if (!query) return true;

  const typeLabel =
    doc.document_type === 'suministro_continuo'
      ? formatDocumentType(doc.real_document_type || 'factura')
      : formatDocumentType(doc.document_type);

  const serieNum =
    doc.series && doc.number
      ? `${doc.series}-${doc.number}`
      : doc.series || doc.number || '';

  const haystack = [
    doc.code,
    doc.series,
    doc.number,
    serieNum,
    typeLabel,
    doc.document_type === 'suministro_continuo' ? 'suministro continuo sc' : '',
    doc.supplier?.name,
    doc.supplier?.document_number,
    doc.supplier?.document_type,
    STATUS_SEARCH_LABELS[doc.status],
    doc.notes,
    doc.integration_source,
    doc.integration_external_reference,
    doc.integration_source_label,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  const tokens = query.split(/\s+/).filter(Boolean);
  return tokens.every((token) => haystack.includes(token));
};

const Documents = () => {
  const { productsProjectEnabled } = useModuleSettings();
  const { siteView, onSiteViewChange } = useComprasSiteViewState();
  const siteListParams = useComprasSiteListParams(siteView);
  const [documents, setDocuments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingDocuments, setLoadingDocuments] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [documentTypeFilter, setDocumentTypeFilter] = useState('all'); // 'all', 'normal', 'suministro_continuo'
  const [userFilter, setUserFilter] = useState('all');
  const [originFilter, setOriginFilter] = useState('all');
  const [projectFilter, setProjectFilter] = useState('all');
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [deleteEntryGuides, setDeleteEntryGuides] = useState(false);

  useEffect(() => {
    loadDocuments();
  }, [projectFilter, productsProjectEnabled, siteView]);

  useEffect(() => {
    if (!productsProjectEnabled) return;
    documentService.getFormData().then((res) => {
      if (res.success) setProjects(res.data?.projects || []);
    }).catch(() => {});
  }, [productsProjectEnabled]);

  const loadDocuments = async () => {
    setLoadingDocuments(true);
    try {
      const params = {};
      if (productsProjectEnabled && projectFilter !== 'all') {
        params.project_id = projectFilter;
      }
      Object.assign(params, siteListParams);
      const response = await documentService.getDocuments(params);
      const rawDocuments = response.success && response.data ? response.data : [];

      const allDocuments = rawDocuments.map((doc) => ({
        ...doc,
        is_supply_continuous: doc.document_type === 'suministro_continuo',
      }));

      setDocuments(sortDocumentsForList(allDocuments));
    } catch (error) {
      console.error('Error al cargar documentos:', error);
      setToast({
        isOpen: true,
        message: 'Error al cargar los documentos',
        type: 'error'
      });
    } finally {
      setLoadingDocuments(false);
    }
  };

  const activeFilters = useMemo(
    () => ({ searchTerm, statusFilter, documentTypeFilter, originFilter, dateFrom, dateTo, selectedSupplier, userFilter }),
    [searchTerm, statusFilter, documentTypeFilter, originFilter, dateFrom, dateTo, selectedSupplier, userFilter]
  );

  const hasActiveFilters =
    searchTerm.trim() !== '' ||
    statusFilter !== 'all' ||
    documentTypeFilter !== 'all' ||
    originFilter !== 'all' ||
    userFilter !== 'all' ||
    dateFrom !== '' ||
    dateTo !== '' ||
    selectedSupplier !== '' ||
    (productsProjectEnabled && projectFilter !== 'all');

  const creatorOptions = useMemo(() => {
    const map = new Map();
    documents.forEach((doc) => {
      const id = doc.creator?.id ?? doc.created_by;
      const name = doc.creator?.name;
      if (id && name && !map.has(String(id))) {
        map.set(String(id), name);
      }
    });
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, 'es'));
  }, [documents]);

  const filteredDocuments = useMemo(
    () => documents.filter((doc) => documentMatchesFilters(doc, activeFilters)),
    [documents, activeFilters]
  );

  // Usar total_amount guardado (incluye ajuste de IGV si se usó "Ajustar montos"); si no hay, recalcular
  const getDocumentDisplayTotal = (doc) => {
    const stored = parseFloat(doc.total_amount);
    if (Number.isFinite(stored) && stored >= 0) return stored;
    const base = parseFloat(doc.base_imponible || 0);
    const noGrav = parseFloat(doc.no_gravados || 0);
    const rate = doc.igv_rate != null ? Number(doc.igv_rate) : 18;
    const igv = Math.round(base * (rate / 100) * 100) / 100;
    return Math.round((base + igv + noGrav) * 100) / 100;
  };

  // Calcular estadísticas sobre el listado filtrado
  const totalDocuments = filteredDocuments.length;
  const issuedDocuments = filteredDocuments.filter((d) => d.status === 'issued').length;
  const cancelledDocuments = filteredDocuments.filter((d) => d.status === 'cancelled').length;
  const totalAmount = filteredDocuments
    .filter((d) => d.status === 'issued')
    .reduce((sum, d) => sum + getDocumentDisplayTotal(d), 0);

  const handleDeleteClick = (document) => {
    setDocumentToDelete(document);
    setDeleteEntryGuides(false);
    setIsDeleteModalOpen(true);
  };

  const linkedEntryGuides = documentToDelete?.entry_guides || [];
  const hasLinkedEntryGuides = linkedEntryGuides.length > 0;

  const handleDeleteConfirm = async () => {
    if (!documentToDelete) return;

    setLoading(true);
    try {
      const response = await documentService.deleteDocument(documentToDelete.id, {
        delete_entry_guides: deleteEntryGuides && hasLinkedEntryGuides,
      });
      await loadDocuments();
      setDocumentToDelete(null);
      setDeleteEntryGuides(false);
      setToast({
        isOpen: true,
        message: response.message || 'Documento eliminado exitosamente',
        type: 'success'
      });
    } catch (error) {
      console.error('Error al eliminar documento:', error);
      setToast({
        isOpen: true,
        message: error.response?.data?.message || error.message || 'Error al eliminar el documento',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePdfSuccess = (doc) => {
    setToast({
      isOpen: true,
      message: `PDF de ${doc.code} descargado`,
      type: 'success',
    });
  };

  const handlePdfError = (message) => {
    setToast({
      isOpen: true,
      message: message || 'Error al generar el PDF',
      type: 'error',
    });
  };

  const renderPdfAction = (document) => (
    <DocumentPdfButton
      documentId={document.id}
      onSuccess={handlePdfSuccess}
      onError={handlePdfError}
    />
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-6">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-800">Documentos</h1>
                <p className="text-gray-600 mt-1">Gestión de documentos (facturas, notas de crédito, suministro continuo, etc.)</p>
              </div>
              <MutateGate>
                <div className="flex gap-2">
                  <Link
                    to="/documents/create"
                    className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md font-medium transition-colors inline-block"
                  >
                    + Nuevo Documento
                  </Link>
                  <Link
                    to="/supply-continuous-documents/create"
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium transition-colors inline-block"
                  >
                    + Suministro Continuo
                  </Link>
                </div>
              </MutateGate>
            </div>

            <ComprasSiteFilterTabs siteView={siteView} onSiteViewChange={onSiteViewChange} />

            {/* Breadcrumb */}
            <nav className="text-sm text-gray-600 mb-4">
              <Link to="/dashboard" className="hover:text-teal-600">Dashboard</Link>
              <span className="mx-2">/</span>
              <Link to="/purchase-orders" className="hover:text-teal-600">Compras</Link>
              <span className="mx-2">/</span>
              <span className="text-gray-800">Documentos</span>
            </nav>

            {/* Filtros, Búsqueda y Rango de Fechas */}
            <div className="bg-white rounded-lg shadow-md p-5 mb-4">
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {/* Filtro por Tipo de Documento */}
                <select
                  value={documentTypeFilter}
                  onChange={(e) => setDocumentTypeFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="all">Todos los Tipos</option>
                  <option value="normal">Documentos Normales</option>
                  <option value="suministro_continuo">Suministro Continuo</option>
                </select>

                {/* Filtro por Origen */}
                <select
                  value={originFilter}
                  onChange={(e) => setOriginFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="all">Todos los Orígenes</option>
                  <option value="integration">Integración ERP</option>
                  <option value="manual">Manual Contify</option>
                </select>

                {/* Filtro por Estado */}
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="all">Todos los Estados</option>
                  <option value="issued">Emitidos</option>
                  <option value="open">Abiertos</option>
                  <option value="completed">Completados</option>
                  <option value="cancelled">Cancelados</option>
                </select>

                {/* Filtro por Usuario */}
                <select
                  value={userFilter}
                  onChange={(e) => setUserFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="all">Todos los Usuarios</option>
                  {creatorOptions.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name}
                    </option>
                  ))}
                </select>

                {productsProjectEnabled && (
                  <ComprasProjectFilterSelect
                    value={projectFilter}
                    onChange={(e) => setProjectFilter(e.target.value)}
                    projects={projects}
                    className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                )}

                {/* Búsqueda (filtro en frontend) */}
                <input
                  type="search"
                  placeholder="Buscar código, serie, proveedor, RUC..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                />

                {/* Fecha Desde */}
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="Fecha desde"
                />

                {/* Fecha Hasta */}
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="Fecha hasta"
                />
              </div>
            </div>

            {hasActiveFilters && documents.length > 0 && (
              <p className="text-sm text-gray-500 mb-3">
                Mostrando {filteredDocuments.length} de {documents.length} documento
                {documents.length === 1 ? '' : 's'}
              </p>
            )}

            {/* Estadísticas */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white rounded-lg shadow-md p-5">
                <div className="text-sm text-gray-600 mb-1">
                  {hasActiveFilters ? 'Coincidencias' : 'Total'}
                </div>
                <div className="text-2xl font-bold text-gray-800">{totalDocuments}</div>
              </div>
              <div className="bg-white rounded-lg shadow-md p-5">
                <div className="text-sm text-gray-600 mb-1">Emitidos</div>
                <div className="text-2xl font-bold text-teal-600">{issuedDocuments}</div>
              </div>
              <div className="bg-white rounded-lg shadow-md p-5">
                <div className="text-sm text-gray-600 mb-1">Cancelados</div>
                <div className="text-2xl font-bold text-gray-500">{cancelledDocuments}</div>
              </div>
              <div className="bg-white rounded-lg shadow-md p-5">
                <div className="text-sm text-gray-600 mb-1">Monto Total</div>
                <div className="text-2xl font-bold text-blue-600">S/ {parseFloat(totalAmount).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
              </div>
            </div>
          </div>

          {/* Tabla de Documentos */}
          {loadingDocuments ? (
            <div className="text-center py-12 bg-white rounded-lg shadow-md">
              <div className="text-gray-500">Cargando documentos...</div>
            </div>
          ) : documents.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg shadow-md">
              <p className="text-gray-500">No se encontraron documentos</p>
            </div>
          ) : filteredDocuments.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg shadow-md">
              <p className="text-gray-500">Ningún documento coincide con los filtros aplicados</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-md overflow-x-auto">
              <TableWithMonthGroups
                data={filteredDocuments}
                getDateForGrouping={(doc) => doc.issue_date}
                defaultCollapsed
                columnCount={productsProjectEnabled ? 11 : 10}
                emptyMessage="No se encontraron documentos"
                headerRow={
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Código</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Tipo</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Serie/Número</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Entidad</th>
                    {productsProjectEnabled && (
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Proyecto</th>
                    )}
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Fecha Emisión</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Órdenes</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Guías</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Monto Total</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Estado</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Acciones</th>
                  </tr>
                }
                renderRow={(document) => {
                  const purchaseOrdersCount = document.purchase_orders_count ?? document.purchase_orders?.length ?? 0;
                  const serviceOrdersCount = document.service_orders_count ?? document.service_orders?.length ?? 0;
                  const continuousServicesCount = document.continuous_services_count ?? document.continuous_services?.length ?? 0;
                  const totalOrdersCount = purchaseOrdersCount + serviceOrdersCount + continuousServicesCount;
                  const entryGuidesCount = document.entry_guides?.length || 0;
                  let usagePercentage = null;
                  if (document.usage_percentage !== null && document.usage_percentage !== undefined) {
                    usagePercentage = parseFloat(document.usage_percentage);
                  } else if (entryGuidesCount > 0) {
                    const warehouseItems = (document.items || []).filter(
                      (item) => item.item_type === 'product' && !item.is_direct_consumption
                    );
                    const totalAmount = warehouseItems.reduce(
                      (sum, item) => sum + parseFloat(item.subtotal || 0),
                      0
                    );
                    let usedAmount = 0;
                    if (document.entry_guides?.length > 0) {
                      document.entry_guides.forEach((guide) => {
                        (guide.items || []).forEach((guideItem) => {
                          const linkedToWarehouseItem = guideItem.document_item_id &&
                            warehouseItems.some((wi) => wi.id === guideItem.document_item_id);
                          const matchesWarehouseProduct = !guideItem.document_item_id &&
                            guideItem.product_id &&
                            warehouseItems.some(
                              (wi) => String(wi.product_id) === String(guideItem.product_id)
                            );
                          if (linkedToWarehouseItem || matchesWarehouseProduct) {
                            usedAmount += parseFloat(guideItem.total || 0);
                          }
                        });
                      });
                    }
                    if (totalAmount > 0) {
                      const diff = Math.abs(totalAmount - usedAmount);
                      usagePercentage = diff <= 0.01
                        ? 100
                        : Math.min(100, Math.round((usedAmount / totalAmount) * 100 * 10) / 10);
                    }
                  }
                  return (
                      <tr key={document.id} className="hover:bg-gray-50">
                        <td className="px-4 py-4 whitespace-nowrap">
                          <Link
                            to={document.document_type === 'suministro_continuo'
                              ? `/supply-continuous-documents/${document.id}`
                              : `/documents/${document.id}/edit`
                            }
                            className="text-sm font-mono text-teal-600 hover:text-teal-900"
                          >
                            {document.code}
                          </Link>
                          {document.integration_source && (
                            <IntegrationDocumentBadge
                              integrationSourceLabel={document.integration_source_label}
                              integrationExternalReference={document.integration_external_reference}
                            />
                          )}
                          <BuyerUserCell creator={document.creator} />
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          {document.document_type === 'suministro_continuo' ? (
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-900">
                                {formatDocumentType(document.real_document_type || 'factura')}
                              </span>
                              <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
                                SC
                              </span>
                            </div>
                          ) : (
                            <span className="text-sm text-gray-900">
                              {formatDocumentType(document.document_type)}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          {document.series || document.number ? (
                            <div className="text-sm text-gray-900">
                              {document.series && document.number 
                                ? `${document.series}-${document.number}`
                                : document.series || document.number || '-'
                              }
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900" title={document.supplier?.name || 'N/A'}>
                            {document.supplier?.name 
                              ? (document.supplier.name.length > 25 
                                  ? `${document.supplier.name.substring(0, 25)}...` 
                                  : document.supplier.name)
                              : 'N/A'}
                          </div>
                          {document.supplier?.document_number && (
                            <div className="text-xs text-gray-500">
                              {document.supplier.document_type === 'ruc' 
                                ? `RUC: ${document.supplier.document_number}`
                                : document.supplier.document_type === 'dni'
                                ? `DNI: ${document.supplier.document_number}`
                                : document.supplier.document_type === 'other'
                                ? `Código: ${document.supplier.document_number}`
                                : `RUC: ${document.supplier.document_number}`
                              }
                            </div>
                          )}
                        </td>
                        {productsProjectEnabled && (
                          <td className="px-4 py-4 text-sm text-gray-600">
                            {formatLineProjectsLabel(document.items)}
                          </td>
                        )}
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDateLocaleLima(document.issue_date)}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                          {totalOrdersCount > 0 ? (
                            <div>
                              {purchaseOrdersCount > 0 && <div>OC: {purchaseOrdersCount}</div>}
                              {serviceOrdersCount > 0 && <div>OS: {serviceOrdersCount}</div>}
                              {continuousServicesCount > 0 && <div>SC: {continuousServicesCount}</div>}
                            </div>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                          {entryGuidesCount > 0 ? (
                            <div className="space-y-1">
                              <div className="text-xs text-gray-600">Guías: {entryGuidesCount}</div>
                              {(usagePercentage !== null && usagePercentage !== undefined) ? (
                                <div className="w-full">
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="text-xs text-gray-500">Uso</span>
                                    <span className="text-xs font-medium text-gray-700">{usagePercentage.toFixed(1)}%</span>
                                  </div>
                                  <div className="w-full bg-gray-200 rounded-full h-2">
                                    <div
                                      className={`h-2 rounded-full transition-all ${
                                        usagePercentage >= 100
                                          ? 'bg-green-500'
                                          : usagePercentage >= 75
                                          ? 'bg-blue-500'
                                          : usagePercentage >= 50
                                          ? 'bg-yellow-500'
                                          : 'bg-orange-500'
                                      }`}
                                      style={{ width: `${Math.min(Math.max(usagePercentage || 0, 0), 100)}%` }}
                                    ></div>
                                  </div>
                                </div>
                              ) : (
                                <div className="text-xs text-gray-400">Sin datos</div>
                              )}
                            </div>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          S/ {getDocumentDisplayTotal(document).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="flex flex-col gap-1">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                              document.status === 'issued' 
                                ? 'bg-green-100 text-green-800' 
                                : document.status === 'open'
                                ? 'bg-yellow-100 text-yellow-800'
                                : document.status === 'completed'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {document.status === 'issued' ? 'Emitido' : 
                               document.status === 'open' ? 'Abierto' :
                               document.status === 'completed' ? 'Completado' :
                               'Cancelado'}
                            </span>
                            {document.integration_source_label && (
                              <span className="text-[10px] text-violet-700 truncate max-w-[120px]" title={document.integration_source_label}>
                                {document.integration_source_label}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center gap-2 flex-wrap">
                            <ViewOrEditLink
                              to={
                                document.document_type === 'suministro_continuo'
                                  ? `/supply-continuous-documents/${document.id}`
                                  : `/documents/${document.id}/edit`
                              }
                              className="text-purple-600 hover:text-purple-900 whitespace-nowrap"
                              editLabel={document.is_integration_read_only ? 'Ver' : 'Editar'}
                              viewLabel="Ver"
                            />
                            {renderPdfAction(document)}
                            {!document.is_integration_read_only && (
                              <MutateGate>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteClick(document)}
                                  className="text-red-600 hover:text-red-900 whitespace-nowrap"
                                  title="Eliminar"
                                >
                                  Eliminar
                                </button>
                              </MutateGate>
                            )}
                          </div>
                        </td>
                      </tr>
                  );
                }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Modal de confirmación de eliminación */}
      <ConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          if (loading) return;
          setIsDeleteModalOpen(false);
          setDocumentToDelete(null);
          setDeleteEntryGuides(false);
        }}
        onConfirm={handleDeleteConfirm}
        title="Eliminar documento"
        message={
          documentToDelete ? (
            <>
              ¿Estás seguro de que deseas eliminar el documento{' '}
              <span className="font-semibold text-gray-900">{documentToDelete.code}</span>?
              <span className="block mt-2 text-gray-500">Esta acción no se puede deshacer.</span>
              {hasLinkedEntryGuides && (
                <div className="mt-4 text-left rounded-md border border-gray-200 bg-gray-50 p-4">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={deleteEntryGuides}
                      onChange={(e) => setDeleteEntryGuides(e.target.checked)}
                      disabled={loading}
                      className="mt-0.5 rounded border-gray-300 text-red-600 focus:ring-red-500"
                    />
                    <span>
                      <span className="block text-sm font-medium text-gray-900">
                        Eliminar también la{linkedEntryGuides.length > 1 ? 's' : ''} guía{linkedEntryGuides.length > 1 ? 's' : ''} asociada{linkedEntryGuides.length > 1 ? 's' : ''}
                      </span>
                      <span className="block mt-1 text-xs text-gray-500">
                        Se eliminarán {linkedEntryGuides.length} guía{linkedEntryGuides.length > 1 ? 's' : ''} y sus movimientos de kardex (afecta stock).
                      </span>
                    </span>
                  </label>
                  <ul className="mt-3 space-y-1 text-xs text-gray-600">
                    {linkedEntryGuides.map((guide) => (
                      <li key={guide.id} className="font-mono">
                        {guide.code}
                        {guide.warehouse?.name ? ` — ${guide.warehouse.name}` : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          ) : (
            ''
          )
        }
        confirmText={loading ? 'Eliminando…' : 'Eliminar'}
        cancelText="Cancelar"
        type="danger"
        isLoading={loading}
      />

      {/* Toast de notificaciones */}
      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />
    </div>
  );
};

export default Documents;

